import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import CrossIcon from "@atlaskit/icon/core/cross";
import {token} from "@atlaskit/tokens";
import type {ReactNode} from "react";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {JourneyProfileFooterMoreActionId} from "~/components/entityProfile/types";
import {StandardModalHeaderDivider} from "~/components/standardModalHeader";

function MoreOptionRowButton({label, onClick}: {label: string; onClick: () => void}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: token("space.150", "12px"),
                height: "40px",
                minHeight: "32px",
                padding: `0 ${token("space.150", "12px")}`,
                borderRadius: "4px",
                background: token("color.background.input"),
                color: token("color.text"),
                cursor: "pointer",
            }}
        >
            <AnankeText color="color.text.subtle">{label}</AnankeText>
            <ChevronRightIcon label="" color={token("color.icon.subtle")} />
        </button>
    );
}

export function resolveJourneyProfileFooterMoreActions(args: {
    showArchive: boolean;
}): Array<{id: JourneyProfileFooterMoreActionId; label: string}> {
    const items: Array<{id: JourneyProfileFooterMoreActionId; label: string}> = [
        {id: "notes", label: "Notes"},
    ];
    if (args.showArchive === true) {
        items.push({id: "archive", label: "Archive"});
    }
    return items;
}

/** More-actions list shared by the mobile bottom sheet and desktop side panel. */
export function JourneyProfileFooterMoreOptionRows({
    items,
    onSelect,
}: {
    items: Array<{id: JourneyProfileFooterMoreActionId; label: string}>;
    onSelect: (actionId: JourneyProfileFooterMoreActionId) => void;
}): ReactNode {
    return (
        <div className="grid gap-ds-space-100" style={{fontFamily: "Inter"}}>
            {items.map((item) => (
                <MoreOptionRowButton
                    key={item.id}
                    label={item.label}
                    onClick={() => {
                        onSelect(item.id);
                    }}
                />
            ))}
        </div>
    );
}

/** More-actions side panel for the journey profile (desktop page portal). */
export function JourneyProfileAsideMoreSurface({
    items,
    onClose,
    onSelect,
}: {
    items: Array<{id: JourneyProfileFooterMoreActionId; label: string}>;
    onClose: () => void;
    onSelect: (actionId: JourneyProfileFooterMoreActionId) => void;
}): ReactNode {
    return (
        <div className="grid h-full min-h-0 min-w-0 w-full grid-rows-[auto_auto_minmax(0,1fr)] overflow-hidden bg-ds-elevation-surface-current">
            <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-200 px-ds-space-250 py-ds-space-150">
                <AnankeHeading size="small">More actions</AnankeHeading>
                <AnankeIconButton
                    type="button"
                    appearance="subtle"
                    icon={CrossIcon}
                    label="Close"
                    onClick={onClose}
                />
            </div>
            <StandardModalHeaderDivider />
            <div className="relative min-h-0 min-w-0 overflow-hidden">
                <div className="absolute inset-0 min-h-0 min-w-0 overflow-y-auto px-ds-space-250 py-ds-space-200">
                    <JourneyProfileFooterMoreOptionRows items={items} onSelect={onSelect} />
                </div>
            </div>
        </div>
    );
}
